// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { ClipboardList, Inbox, Shield, Pill, Truck, PauseCircle, RefreshCcw, LayoutList, Settings2 } from 'lucide-react'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { useModules } from '../../auth/ModulesContext'
import PioneerRxSourceBanner from '../../components/workflow/PioneerRxSourceBanner'

// ----------------------------------------------------------------------
// Counts context — fetched once at the layout level and shared with tabs.
// Tabs call refresh() after any successful state transition so the badge
// numbers stay current without polling.
// ----------------------------------------------------------------------

export interface PharmacyQueueCounts {
  awaiting_verification: number
  verified: number
  ready_to_fill: number
  pickup_or_delivery: number
  on_hold: number
  pending_refills: number
  // RX-PERF-13 (audit F12/F14) — optional so a client deployed ahead of the API degrades to
  // a blank badge rather than NaN. total = grain-consistent All-tab count; payer_blocked =
  // the Awaiting-Insurance badge (replaces the dedicated ?payer_blocked=1 page query).
  total?: number
  payer_blocked?: number
}

interface PharmacyContextValue {
  counts: PharmacyQueueCounts | null
  refresh: () => Promise<void>
}

const PharmacyContext = createContext<PharmacyContextValue | null>(null)

export function usePharmacy(): PharmacyContextValue {
  const ctx = useContext(PharmacyContext)
  if (!ctx) throw new Error('usePharmacy must be used inside <PharmacyLayout>')
  return ctx
}

// ----------------------------------------------------------------------
// Layout
// ----------------------------------------------------------------------

export default function PharmacyLayout({ children }: { children?: ReactNode }) {
  const { me } = useAuth()
  const { isDisabled: isModuleDisabled } = useModules()
  const has = (p: string) => Boolean(me?.permissions?.includes(p))

  // RX-PERF-10: layout data lives in TanStack Query. History that shaped these options:
  // the insurance badge once downloaded the ENTIRE capped /rx list (818 KB) to count it —
  // it is a page_size=1 exact-total read (RX-PERF-1); the config fetches once re-fired on
  // every tab mount (RX-PERF-7). Queries dedupe concurrent mounts, cache across remounts,
  // refetch on focus, and poll badges on a slow interval so they stay live without any
  // tab-mount side effects.
  const queryClient = useQueryClient()
  // BADGE-SAFE (P0 2026-08-06 postmortem — Azure SQL 10928, tenant DB worker-cap exhaustion):
  // the 10s badge cadence multiplied COUNT load 6× per user and took ASP down at afternoon
  // load. Cadence is a LOAD MULTIPLIER (cadence × views × users vs the DB tier's worker cap):
  // badges poll at 60s, freshness on tab entry comes from the navigation invalidation below,
  // and the server memoizes count_only results so N users collapse to ~1 query per view per
  // memo window. Never lower these intervals without sizing against the DB tier.
  const { data: counts = null } = useQuery({
    queryKey: ['rx', 'counts'],
    queryFn: async () => {
      const r = await api.apiFetch(`/rx/counts`, { credentials: 'include' })
      if (!r.ok) throw new Error(`counts ${r.status}`)
      return (await r.json()).queue as PharmacyQueueCounts
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  // RX-PERF-13 (audit F12): the insurance badge now rides /rx/counts (one aggregate round
  // trip) instead of a dedicated ?payer_blocked=1 page query — that query COUNTed + paged an
  // unindexable predicate over the whole tenant every 60s (~8s observed on ASP).
  const { data: customViews = [] } = useQuery({
    queryKey: ['views'],
    queryFn: () => api.listViews().then(r => r.items),
    staleTime: 120_000,
  })
  // Server-side tab access control. null = not loaded yet, show all (permission gate only).
  const { data: serverVisibleTabs = null } = useQuery({
    queryKey: ['tabs', 'my'],
    queryFn: () => api.getMyTabs().then(r => new Set(r.visible_tabs)),
    staleTime: 120_000,
  })
  // Custom-view record counts for the tab badges — D-BADGE-1: ONE batched call
  // (GET /views/counts) returns every badge total this user may see, flat regardless of how
  // many views the pharmacy configures. The server serves memoized totals and executes memo
  // misses at concurrency ≤ 2, so the cold window claims 1–2 tenant-DB workers instead of N —
  // the concurrency decision lives server-side where a stale browser bundle can't ignore it
  // (P0 2026-08-06 lesson: server-enforced beats client-promised).
  // BADGE-SAFE (crawl finding): only poll views whose TAB this user can actually see —
  // RBAC-hidden tabs were being counted anyway, pure waste against the DB worker budget.
  const badgeViews = useMemo(
    () => (isModuleDisabled('queues') ? [] : customViews.filter(v => !serverVisibleTabs || serverVisibleTabs.has(`cv_${v.id}`))),
    [customViews, serverVisibleTabs, isModuleDisabled])
  // Deploy-order fallback (mandatory, one release only): SWA frontend and Functions deploy
  // near-simultaneously but browsers hold stale bundles — a fresh bundle can meet an older
  // backend where /views/counts 404s. Fall back to the per-view count_only path for the
  // session rather than blanking every badge. Remove with the per-view queries below once
  // the endpoint has shipped a release.
  const [batchUnsupported, setBatchUnsupported] = useState(false)
  const { data: viewCountsResp } = useQuery({
    queryKey: ['views', 'counts-batch'],
    queryFn: async () => {
      try { return await api.getViewCounts() }
      catch (e) {
        if (e instanceof api.ApiError && e.status === 404) { setBatchUnsupported(true); return null }
        throw e
      }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: badgeViews.length > 0 && !batchUnsupported,
  })
  // Fallback path only (older backend): per-view count_only=1 — the composed COUNT, exact
  // total, same predicate as the grid (ASP-53); server-memoized (BADGE-SAFE). limit:1 rides
  // along so an even older API (param ignored) still answers with the same total.
  const viewCountQueries = useQueries({
    queries: (batchUnsupported ? badgeViews : []).map(v => ({
      queryKey: ['views', v.id, 'count'],
      queryFn: () => api.executeView(v.id, { limit: 1, countOnly: true }).then(d => (typeof d.total === 'number' ? d.total : null)),
      refetchInterval: 60_000,
      staleTime: 30_000,
    })),
  })
  const viewCounts: Record<string, number | null> = {}
  badgeViews.forEach((v, i) => {
    viewCounts[v.id] = batchUnsupported
      ? (viewCountQueries[i]?.data ?? null)
      // A failed view arrives as null (badge renders blank) — one broken view never blanks
      // every badge.
      : (viewCountsResp?.counts?.[v.id]?.total ?? null)
  })

  // BADGE-SAFE: entering any tab refetches every badge NOW — a queue you just opened must not
  // show a stale count. This is what buys freshness at the 60s poll cadence, and the server
  // memo keeps it cheap (a navigation burst mostly serves memoized totals). The COUNT queries
  // are predicate-targeted so the cached /views LIST (and other config) is NOT refetched here.
  const { pathname } = useLocation()
  const firstNav = useRef(true)
  useEffect(() => {
    if (firstNav.current) { firstNav.current = false; return }  // mount already fetches fresh
    void queryClient.invalidateQueries({
      predicate: q =>
        (q.queryKey[0] === 'rx' && q.queryKey[1] === 'counts') ||
        // D-BADGE-1: the batch badge query replaces the per-view ['views', id, 'count']
        // match; the per-view arm stays ONLY for the 404 deploy-order fallback and retires
        // with it.
        (q.queryKey[0] === 'views' && (q.queryKey[1] === 'counts-batch' || q.queryKey[2] === 'count')),
    })
  }, [pathname, queryClient])

  // Tabs call refresh() after a successful state transition; invalidation refetches the
  // active badge queries (deduped by the client) — same contract, no hand-rolled guard.
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['rx', 'counts'] })
  }, [queryClient])

  // Server RBAC check: if loaded and tab has RBAC, check membership
  // Server RBAC: if loaded and tab not in list, hide it
  const canSeeTab = (key: string, permGate: boolean) => {
    if (!permGate) return false // permission gate always applies
    if (!serverVisibleTabs) return permGate // not loaded yet, use permission gate only
    return serverVisibleTabs.has(key)
  }

  // Operation-queue + custom-view tabs are the "queues" module. When it's disabled, the
  // Prescriptions page collapses to a single flat "All" list — no workflow tabs, no custom views.
  const queuesOn = !isModuleDisabled('queues')
  const allTabs: { key: string; to: string; label: string; icon: any; count?: number | null; show: boolean; end?: boolean; isCustom?: boolean }[] = [
    { key: 'intake',     to: '/rx/intake',     label: 'Intake',             icon: Inbox,         count: counts?.awaiting_verification, show: queuesOn && canSeeTab('intake', has('rx.verify')) },
    { key: 'insurance',  to: '/rx/insurance',  label: 'Awaiting insurance', icon: Shield,        count: counts?.payer_blocked,         show: queuesOn && canSeeTab('insurance', has('rx.read')) },
    { key: 'production', to: '/rx/production', label: 'Production',         icon: Pill,          count: counts?.ready_to_fill,         show: queuesOn && canSeeTab('production', has('rx.fill')) },
    { key: 'ready',      to: '/rx/ready',      label: 'Ready & out',        icon: Truck,         count: counts?.pickup_or_delivery,    show: queuesOn && canSeeTab('ready', has('rx.dispense')) },
    { key: 'hold',       to: '/rx/hold',       label: 'On hold',            icon: PauseCircle,   count: counts?.on_hold,               show: queuesOn && canSeeTab('hold', has('rx.read')) },
    { key: 'refills',    to: '/rx/refills',    label: 'Refills',            icon: RefreshCcw,    count: counts?.pending_refills,       show: queuesOn && canSeeTab('refills', has('refills.read')) },
    // RX-PERF-13 (audit F14): the All tab finally gets a badge — the server's grain-consistent
    // total from /rx/counts (NEVER a client-side sum of by_state: at fill grain that misses
    // fill-less rx and drifts from the pager).
    { key: 'all',        to: '/rx',            label: 'All',                icon: ClipboardList, count: counts?.total, show: canSeeTab('all', true), end: true },
    ...customViews.map(v => ({
      key: `cv_${v.id}`, to: `/rx/view/${v.id}`, label: v.name, icon: LayoutList,
      // Record count badge on every custom-view tab (exact server total via limit=1).
      count: viewCounts[v.id],
      show: queuesOn && (!serverVisibleTabs || serverVisibleTabs.has(`cv_${v.id}`)),
      isCustom: true,
    })),
  ].filter(t => t.show)

  // Per-user tab prefs (localStorage)
  const TABS_KEY = 'owl_rx_tabs'
  const [tabPrefs, setTabPrefs] = useState<{ hidden: string[]; order: string[] }>(() => {
    try { const s = localStorage.getItem(TABS_KEY); return s ? JSON.parse(s) : { hidden: [], order: [] } } catch { return { hidden: [], order: [] } }
  })
  const [showTabManager, setShowTabManager] = useState(false)

  const saveTabPrefs = (p: typeof tabPrefs) => {
    setTabPrefs(p); try { localStorage.setItem(TABS_KEY, JSON.stringify(p)) } catch {}
  }

  // Apply user prefs: reorder + hide
  const visibleTabs = useMemo(() => {
    const hiddenSet = new Set(tabPrefs.hidden)
    const allKeys = allTabs.map(t => t.key)
    const ordered = tabPrefs.order.filter(k => allKeys.includes(k))
    for (const k of allKeys) { if (!ordered.includes(k)) ordered.push(k) }
    return ordered.map(k => allTabs.find(t => t.key === k)!).filter(t => t && !hiddenSet.has(t.key))
  }, [allTabs, tabPrefs])

  // All tabs in user's order (for the manager panel)
  const allInOrder = useMemo(() => {
    const allKeys = allTabs.map(t => t.key)
    const ordered = tabPrefs.order.filter(k => allKeys.includes(k))
    for (const k of allKeys) { if (!ordered.includes(k)) ordered.push(k) }
    return ordered.map(k => allTabs.find(t => t.key === k)!).filter(Boolean)
  }, [allTabs, tabPrefs.order])

  const isTabHidden = (key: string) => tabPrefs.hidden.includes(key)
  const toggleTabHide = (key: string) => {
    const next = { ...tabPrefs, hidden: isTabHidden(key) ? tabPrefs.hidden.filter(k => k !== key) : [...tabPrefs.hidden, key] }
    saveTabPrefs(next)
  }
  const moveTabUp = (key: string) => {
    const arr = allInOrder.map(t => t.key)
    const idx = arr.indexOf(key); if (idx <= 0) return
    ;[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]
    saveTabPrefs({ ...tabPrefs, order: arr })
  }
  const moveTabDown = (key: string) => {
    const arr = allInOrder.map(t => t.key)
    const idx = arr.indexOf(key); if (idx < 0 || idx >= arr.length - 1) return
    ;[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]
    saveTabPrefs({ ...tabPrefs, order: arr })
  }
  const resetTabs = () => saveTabPrefs({ hidden: [], order: [] })

  return (
    <PharmacyContext.Provider value={{ counts, refresh }}>
      <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <header style={headerStyle}>
          <div style={{ maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%', padding: '24px 24px 0' }}>
            <h1 style={titleStyle}>Prescriptions</h1>
            {allTabs.length > 1 && (<>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <nav style={{ ...tabRow, flex: 1 }} aria-label="Prescription views">
                {visibleTabs.map(t => {
                  const Icon = t.icon
                  return (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      end={t.end ?? false}
                      style={({ isActive }) => ({
                        ...tabStyle,
                        ...(isActive ? tabActiveStyle : null),
                      })}
                    >
                      <Icon size={15} aria-hidden="true" />
                      <span>{t.label}</span>
                      {/* Queue tabs badge only when non-zero (long-standing convention);
                          custom views always show their record count — 0 is information. */}
                      {typeof t.count === 'number' && (t.count > 0 || (t as any).isCustom) && (
                        <span style={badgeStyle}>{t.count.toLocaleString()}</span>
                      )}
                    </NavLink>
                  )
                })}
              </nav>
              <button onClick={() => setShowTabManager(!showTabManager)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: 'var(--color-text-soft)' }}
                title="Manage tabs">
                <Settings2 size={16} />
              </button>
            </div>
            {showTabManager && (
              <TabManagerPanel
                allInOrder={allInOrder}
                isTabHidden={isTabHidden}
                toggleTabHide={toggleTabHide}
                moveTabUp={moveTabUp}
                moveTabDown={moveTabDown}
                resetTabs={resetTabs}
                onClose={() => setShowTabManager(false)}
              />
            )}
            </>)}
          </div>
        </header>

        <div style={{ flex: 1, padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
          <PioneerRxSourceBanner variant="queue" />
          {children ?? <Outlet />}
        </div>
      </div>
    </PharmacyContext.Provider>
  )
}

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  borderBottom: '1px solid var(--color-border)',
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700,
  margin: 0, letterSpacing: -0.5,
}
const tabRow: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  marginTop: 16,
  flexWrap: 'wrap',
}
const tabStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 14px',
  fontSize: 13,
  color: 'var(--color-text-soft)',
  textDecoration: 'none',
  borderBottom: '2px solid transparent',
  marginBottom: -1,
  fontWeight: 500,
}
const tabActiveStyle: React.CSSProperties = {
  color: 'var(--color-text)',
  borderBottomColor: 'var(--color-primary)',
}
const badgeStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 4,
  padding: '1px 7px',
  background: 'var(--color-primary)',
  color: '#fff',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.5,
}
const mgrBtn: React.CSSProperties = {
  fontSize: 11, padding: '3px 8px', background: 'var(--color-surface)',
  border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
}
const arrowBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 12,
}

// ── Tab Manager Panel (personal layout only — RBAC is in Staff) ────
function TabManagerPanel({ allInOrder, isTabHidden, toggleTabHide, moveTabUp, moveTabDown, resetTabs, onClose }: {
  allInOrder: { key: string; label: string; isCustom?: boolean }[]
  isTabHidden: (key: string) => boolean
  toggleTabHide: (key: string) => void
  moveTabUp: (key: string) => void
  moveTabDown: (key: string) => void
  resetTabs: () => void
  onClose: () => void
}) {
  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 10, marginTop: 6, maxWidth: 400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>My tab layout</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={resetTabs} style={mgrBtn}>Reset</button>
          <button onClick={onClose} style={mgrBtn}>Done</button>
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '0 0 6px' }}>
        Reorder and hide tabs for your personal view. To control who can access tabs, go to Staff → user detail.
      </p>
      {allInOrder.map((t, idx) => (
        <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 12, opacity: isTabHidden(t.key) ? 0.4 : 1 }}>
          <button onClick={() => moveTabUp(t.key)} disabled={idx === 0} style={arrowBtn}>↑</button>
          <button onClick={() => moveTabDown(t.key)} disabled={idx === allInOrder.length - 1} style={arrowBtn}>↓</button>
          <button onClick={() => toggleTabHide(t.key)} style={arrowBtn}>{isTabHidden(t.key) ? '◻' : '☑'}</button>
          <span>{t.label}</span>
          {t.isCustom && <span style={{ fontSize: 9, color: 'var(--color-text-mute)', marginLeft: 4 }}>custom</span>}
        </div>
      ))}
    </div>
  )
}
